"use client";

import { useEffect, useRef, useState } from "react";
import { MapMarker, Location } from "@/types";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { COLOURS } from "@/lib/constants";
import { useGoogleMaps } from "@/components/providers/googleMapsProvider";

interface MapProps {
  markers: MapMarker[];
  selectedAddresses: string[];
  userLocation?: Location | null;
  maxDistance?: number;
}

// Define the base class for CustomInfoWindow
type CustomInfoWindowClass = new (
  position: google.maps.LatLng,
  content: HTMLElement
) => google.maps.OverlayView & {
  open(): void;
  close(): void;
};

// Store info windows in a WeakMap to avoid using deprecated get/set methods
const infoWindowMap = new WeakMap<
  google.maps.marker.AdvancedMarkerElement,
  InstanceType<CustomInfoWindowClass>
>();

// Define the CustomInfoWindow class type after google maps is loaded
let CustomInfoWindow: CustomInfoWindowClass | null = null;

export default function Map({
  markers,
  selectedAddresses,
  userLocation,
  maxDistance,
}: MapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const userMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(
    null
  );
  const radiusCircleRef = useRef<google.maps.Circle | null>(null);
  const { isLoaded, loadError } = useGoogleMaps();

  // Define CustomInfoWindow class when Google Maps is loaded
  useEffect(() => {
    if (!isLoaded || CustomInfoWindow) return;

    CustomInfoWindow = class extends google.maps.OverlayView {
      private position: google.maps.LatLng;
      private content: HTMLElement;
      private isOpen: boolean = false;

      constructor(position: google.maps.LatLng, content: HTMLElement) {
        super();
        this.position = position;
        this.content = content;
      }

      onAdd() {
        const panes = this.getPanes();
        if (panes) {
          this.content.style.position = "absolute";
          this.content.style.visibility = "hidden";
          this.content.style.zIndex = "1000";
          panes.floatPane.appendChild(this.content);
        }
      }

      draw() {
        const overlayProjection = this.getProjection();
        if (!overlayProjection || !this.isOpen) return;

        const position = overlayProjection.fromLatLngToDivPixel(this.position);
        if (position) {
          const isUserLocation =
            this.content.innerHTML.includes("Your Location");

          this.content.style.left = `${position.x - 60}px`;
          this.content.style.top = `${
            position.y - (isUserLocation ? 90 : 160)
          }px`;
          this.content.style.visibility = this.isOpen ? "visible" : "hidden";
        }
      }

      open() {
        this.isOpen = true;
        this.setMap(this.getMap());
        this.draw();
      }

      close() {
        this.isOpen = false;
        this.content.style.visibility = "hidden";
      }

      onRemove() {
        if (this.content.parentElement) {
          this.content.parentElement.removeChild(this.content);
        }
      }
    };
  }, [isLoaded]);

  useEffect(() => {
    let isMounted = true;

    // Clean up function for markers and map
    const cleanup = () => {
      // Clear all markers except user marker
      markersRef.current.forEach((marker) => {
        const infoWindow = infoWindowMap.get(marker);
        if (infoWindow) {
          infoWindow.close();
          infoWindow.setMap(null);
        }
        marker.map = null;
      });
      markersRef.current = [];
    };

    const initMap = async () => {
      try {
        if (!isLoaded || !mapRef.current || !CustomInfoWindow) return;
        cleanup(); // Clean up before initializing

        // Import the marker library
        const { AdvancedMarkerElement, PinElement } =
          (await google.maps.importLibrary(
            "marker"
          )) as google.maps.MarkerLibrary;

        // Create map instance first
        if (!mapInstanceRef.current && mapRef.current) {
          mapInstanceRef.current = new google.maps.Map(mapRef.current, {
            center: markers[0]?.position || {
              lat: -32.06325055886422,
              lng: 115.91268655742262,
            },
            zoom: 16,
            mapTypeControl: false,
            streetViewControl: false,
            disableDefaultUI: true,
            zoomControl: true,
            scaleControl: true,
            backgroundColor: "#050505",
            mapId: "advanced_markers", // Required for advanced markers
          });

          // Add a listener for when the map is fully loaded
          google.maps.event.addListenerOnce(
            mapInstanceRef.current,
            "tilesloaded",
            () => {
              if (isMounted) {
                setIsLoading(false);
              }
            }
          );
        }

        const map = mapInstanceRef.current;
        if (!map) return;

        // Create new markers
        markers.forEach((markerData) => {
          // Skip if this is a user location marker
          if (
            userLocation &&
            markerData.position.lat === userLocation.latitude &&
            markerData.position.lng === userLocation.longitude
          ) {
            return;
          }

          const isSelected = selectedAddresses.includes(markerData.id);

          // Create a custom pin element
          const pinElement = new PinElement({
            background: isSelected ? "#ef4444" : COLOURS.primary,
            borderColor: "white",
            glyph: "",
            scale: 0.8,
          });

          const marker = new AdvancedMarkerElement({
            map,
            position: markerData.position,
            title: markerData.title,
            content: pinElement.element,
          });

          // Create custom info window content
          const content = document.createElement("div") as HTMLElement;
          content.className = "custom-info-window";
          content.innerHTML = `
            <div class="bg-white rounded-lg shadow-lg overflow-hidden transition-all duration-200 cursor-pointer relative group">
              <button class="close-button absolute top-2 right-2 z-10 p-1 hover:bg-gray-100 rounded-full text-gray-600">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
              <div class="flex items-center w-[120px] group-hover:w-[420px] transition-[width] duration-300 ease-out">
                <div class="w-[120px] h-[120px] flex-shrink-0 p-3 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-0">
                  ${
                    markerData.logo
                      ? `<img src="${markerData.logo}" alt="${markerData.title}" class="w-full h-full object-contain rounded" />`
                      : `<div class="w-full h-full flex items-center justify-center rounded">
                          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <rect x="4" y="4" width="16" height="16" rx="2" ry="2"></rect>
                            <path d="M9 9h6v6H9z"></path>
                            <path d="M15 15v-2a2 2 0 0 0-2-2H9"></path>
                          </svg>
                        </div>`
                  }
                </div>
                <div class="hover-content w-0 group-hover:w-[300px] overflow-hidden transition-[width] duration-300 ease-out">
                  <div class="p-4 space-y-2 border-l border-gray-200 min-w-[300px]">
                    <h3 class="font-semibold text-lg text-gray-900">${
                      markerData.title
                    }</h3>
                    <div class="space-y-1">
                      <p class="text-sm text-gray-900">${
                        markerData.description || ""
                      }</p>
                      <p class="text-sm text-gray-600">${
                        markerData.address || ""
                      }</p>
                    </div>
                    ${
                      markerData.distance !== null &&
                      markerData.distance !== undefined
                        ? `<p class="text-sm text-gray-600">Distance: ${markerData.distance.toFixed(
                            1
                          )}km</p>`
                        : ""
                    }
                  </div>
                </div>
              </div>
            </div>
          `;

          // Remove the hover effects JavaScript since we're showing all content at once
          const closeButton = content.querySelector(".close-button");
          if (closeButton) {
            closeButton.addEventListener("click", (e: Event) => {
              e.preventDefault();
              e.stopPropagation();
              customInfoWindow.close();
              customInfoWindow.setMap(null);
            });
          }

          // Create and set up custom info window
          if (!CustomInfoWindow) {
            console.error("CustomInfoWindow is not initialized");
            return;
          }

          const customInfoWindow = new CustomInfoWindow(
            new google.maps.LatLng(
              markerData.position.lat,
              markerData.position.lng
            ),
            content
          );

          // Click handler
          marker.addEventListener("gmp-click", () => {
            // Close all other info windows
            markersRef.current.forEach((m) => {
              const markerInfo = infoWindowMap.get(m);
              if (markerInfo) {
                markerInfo.close();
                markerInfo.setMap(null);
              }
            });

            // Set map and open this info window
            if (!customInfoWindow.getMap()) {
              customInfoWindow.setMap(map);
            }
            customInfoWindow.open();
          });

          // Store the info window with the marker for reference
          infoWindowMap.set(marker, customInfoWindow);

          markersRef.current.push(marker);
        });

        // Handle bounds and zoom
        if (selectedAddresses.length > 0) {
          const selectedBounds = new google.maps.LatLngBounds();
          markers
            .filter((marker) => selectedAddresses.includes(marker.id))
            .forEach((marker) => {
              selectedBounds.extend(marker.position);
            });

          if (selectedAddresses.length === 1) {
            const selectedMarker = markers.find((marker) =>
              selectedAddresses.includes(marker.id)
            );
            if (selectedMarker) {
              map.setCenter(selectedMarker.position);
              map.setZoom(12);
            }
          } else {
            map.fitBounds(selectedBounds);
          }
        } else if (markers.length > 1) {
          const bounds = new google.maps.LatLngBounds();
          markers.forEach((marker) => bounds.extend(marker.position));
          map.fitBounds(bounds);
        }

        // Set loading to false after everything is initialized
        if (isMounted) {
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error initializing map:", error);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initMap();

    return () => {
      isMounted = false;
      cleanup();
    };
  }, [markers, selectedAddresses, isLoaded, userLocation]);

  // Handle user location in a separate effect
  useEffect(() => {
    // Clear user marker, info window, and radius circle when location changes
    const cleanup = async () => {
      if (userMarkerRef.current) {
        const infoWindow = infoWindowMap.get(userMarkerRef.current);
        if (infoWindow) {
          infoWindow.close();
          infoWindow.setMap(null);
        }
        userMarkerRef.current.map = null;
        userMarkerRef.current = null;
      }

      // Clear radius circle
      if (radiusCircleRef.current) {
        radiusCircleRef.current.setMap(null);
        radiusCircleRef.current = null;
      }
    };

    const initUserMarker = async () => {
      await cleanup(); // Clean up before adding new marker

      // Add user location marker if available
      if (userLocation && mapInstanceRef.current && isLoaded) {
        const { AdvancedMarkerElement, PinElement } =
          (await google.maps.importLibrary(
            "marker"
          )) as google.maps.MarkerLibrary;

        const userPosition = {
          lat: userLocation.latitude,
          lng: userLocation.longitude,
        };

        // Create user location info window content
        const userContent = document.createElement("div") as HTMLElement;
        userContent.className = "custom-info-window";
        userContent.innerHTML = `
          <div class="bg-emerald-600 rounded-lg shadow-lg overflow-hidden p-4 relative">
            <button class="close-button absolute top-2 right-2 z-10 p-1 hover:bg-emerald-700 rounded-full text-white">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            <div class="space-y-2">
              <div class="flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                  <circle cx="12" cy="10" r="3"></circle>
                </svg>
                <p class="text-sm font-medium text-white">Your Location</p>
              </div>
              <p class="text-sm text-white/90">${userLocation.address || ""}</p>
            </div>
          </div>
        `;

        // Create and set up custom info window
        if (!CustomInfoWindow) {
          console.error("CustomInfoWindow is not initialized");
          return;
        }

        const userInfoWindow = new CustomInfoWindow(
          new google.maps.LatLng(userLocation.latitude, userLocation.longitude),
          userContent
        );

        // Create a custom pin element for user location
        const pinElement = new PinElement({
          background: "#059669",
          borderColor: "white",
          glyph: "",
          scale: 0.9,
        });

        // Create new user marker
        userMarkerRef.current = new AdvancedMarkerElement({
          map: mapInstanceRef.current,
          position: userPosition,
          title: "Your Location",
          content: pinElement.element,
          zIndex: 1000,
        });

        // Store the info window with the marker
        infoWindowMap.set(userMarkerRef.current, userInfoWindow);

        // Add click handler for user marker
        userMarkerRef.current.addEventListener("gmp-click", () => {
          // Close all other info windows
          markersRef.current.forEach((m) => {
            const markerInfo = infoWindowMap.get(m);
            if (markerInfo) {
              markerInfo.close();
              markerInfo.setMap(null);
            }
          });

          userInfoWindow.setMap(mapInstanceRef.current);
          userInfoWindow.open();
        });

        // Add close button handler
        const closeButton = userContent.querySelector(".close-button");
        if (closeButton) {
          closeButton.addEventListener("click", (e: Event) => {
            e.preventDefault();
            e.stopPropagation();
            userInfoWindow.close();
            userInfoWindow.setMap(null);
          });
        }

        // Create new radius circle
        if (maxDistance && maxDistance > 0) {
          radiusCircleRef.current = new google.maps.Circle({
            map: mapInstanceRef.current,
            center: userPosition,
            radius: maxDistance * 1000,
            fillColor: COLOURS.primary,
            fillOpacity: 0.05,
            strokeColor: COLOURS.primary,
            strokeOpacity: 0.2,
            strokeWeight: 5,
          });
        }

        // Center map on user location if no addresses are selected
        if (selectedAddresses.length === 0) {
          mapInstanceRef.current.setCenter(userPosition);
          mapInstanceRef.current.setZoom(12);
        }
      }
    };

    initUserMarker();

    return () => {
      cleanup(); // Clean up on unmount or when dependencies change
    };
  }, [userLocation, selectedAddresses, maxDistance, isLoaded]);

  // Update circle properties if it exists
  useEffect(() => {
    if (radiusCircleRef.current && mapInstanceRef.current) {
      if (maxDistance && maxDistance > 0) {
        radiusCircleRef.current.setRadius(maxDistance * 1000);
        radiusCircleRef.current.setVisible(true);

        // Get circle bounds and fit map to it
        const bounds = radiusCircleRef.current.getBounds();
        if (bounds) {
          mapInstanceRef.current.fitBounds(bounds, {
            top: 50,
            right: 50,
            bottom: 50,
            left: 50,
          });
        }
      } else {
        radiusCircleRef.current.setVisible(false);
      }
    }
  }, [maxDistance]);

  if (loadError) {
    return (
      <div className='flex items-center justify-center w-full h-full bg-black text-red-500'>
        {loadError}
      </div>
    );
  }

  return (
    <div className='relative w-full h-full bg-[#0a0a0a]'>
      <div ref={mapRef} className='w-full h-full' />
      {isLoading && (
        <div className='absolute inset-0 flex items-center justify-center bg-black'>
          <LoadingSpinner />
        </div>
      )}
    </div>
  );
}
